import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Stack,
  Text,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import TradingSpinner from "../TradingSpinner";
import AccountValueSyncToggle from "../common/AccountValueSyncToggle";

const BulkUpdateCsvView = ({
  cancelRef,
  selectedFile,
  csvFixLoading,
  tradeLoading,
  accountValueUpdateCSVEnable,
  isFreeUser,
  aiCsvQueriesRemaining,
  csvQuotaExhausted,
  onFileChange,
  onCheckboxChange,
  onCancel,
  onConfirm,
  onFixWithAI,
}) => {
  const { colorMode } = useColorMode();

  return (
    <Box
      minW={{ base: "90%", md: "468px" }}
      maxW="650px"
      rounded="lg"
      overflow="hidden"
      boxShadow="sm"
      position="relative"
    >
      {csvFixLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor={colorMode === "light" ? "whiteAlpha.800" : "gray.900"}
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="lg"
        >
          <VStack spacing={4}>
            <TradingSpinner size="40px" />
            <Text fontSize="md" fontWeight="semibold" textAlign="center">
              AI Agent is running...
            </Text>
            <Text
              fontSize="sm"
              color={colorMode === "light" ? "gray.600" : "gray.400"}
              textAlign="center"
            >
              This may take up to 2 minutes
            </Text>
          </VStack>
        </Box>
      )}
        <Stack
          spacing={4}
          p="1rem"
          backgroundColor={
            colorMode === "light" ? "whiteAlpha.900" : "whiteAlpha.100"
          }
          boxShadow="md"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Heading
              fontSize="xl"
              fontWeight="bold"
              fontFamily="'Open Sans', sans-serif"
              color={colorMode === "light" ? "gray.700" : "gray.200"}
            >
              CSV Bulk Update
            </Heading>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="gray"
              borderRadius="md"
              onClick={onCancel}
            >
              Back
            </Button>
          </Flex>
          <Box>
            <Heading size="xs" textTransform="uppercase" color={colorMode === "light" ? "gray.600" : "gray.300"} mb={2}>
              Steps
            </Heading>
            <OrderedList spacing={1} pl={5}>
              <ListItem>Export desired trades from Summary Table</ListItem>
              <ListItem>Make necessary changes to each trade entry</ListItem>
              <ListItem>Delete any rows you are not making changes to</ListItem>
              <ListItem>Attach CSV file below and click submit</ListItem>
            </OrderedList>
          </Box>

          <Box bg={colorMode === "light" ? "yellow.50" : "yellow.900"} borderLeftWidth="4px" borderLeftColor="yellow.400" rounded="md" p={3}>
            <Heading size="xs" textTransform="uppercase" color={colorMode === "light" ? "yellow.800" : "yellow.200"} mb={2}>
              Tip
            </Heading>
            <Text fontSize="sm">
              To clear a column on a trade, set its cell value to <strong>None</strong>.
            </Text>
          </Box>

          <HStack spacing={3}>
            <input
              type="file"
              accept=".csv"
              hidden
              id="csv-bulk-upload"
              onChange={onFileChange}
            />
            <Button
              variant="outline"
              colorScheme="blue"
              borderRadius="md"
              size="sm"
              onClick={() => document.getElementById("csv-bulk-upload").click()}
            >
              Choose File
            </Button>
            <Button
              as={Link}
              href="./csv/bulk_update_example.csv"
              download="example_bulk_trade_updates.csv"
              variant="outline"
              colorScheme="gray"
              borderRadius="md"
              size="sm"
              _hover={{ textDecoration: "none" }}
            >
              ⬇ Download example
            </Button>
            {selectedFile && (
              <Text fontSize="xs" color="gray.500">
                {selectedFile.name}
              </Text>
            )}
            {tradeLoading || csvFixLoading ? (
              <VStack align="flex-start" spacing={1} paddingLeft={3}>
                {csvFixLoading && (
                  <>
                    <Text color="blue.500" fontWeight="semibold">
                      AI Agent is running...
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      This may take up to 2 minutes
                    </Text>
                  </>
                )}
                {tradeLoading && !csvFixLoading && (
                  <Text color="red.500">Loading your Trades...</Text>
                )}
              </VStack>
            ) : (
              <Text></Text>
            )}
          </HStack>
          <AccountValueSyncToggle
            isChecked={accountValueUpdateCSVEnable}
            onChange={(e) => onCheckboxChange(e, "UpdateCSV")}
            actionLabel="these CSV updates"
            pl={2}
          />
          {isFreeUser &&
            aiCsvQueriesRemaining !== null &&
            aiCsvQueriesRemaining > 0 && (
              <Text
                fontSize="xs"
                color={colorMode === "light" ? "gray.500" : "gray.400"}
                mt={2}
                ml={2}
              >
                {aiCsvQueriesRemaining}/10 AI queries remaining
              </Text>
            )}
          {csvQuotaExhausted && (
            <Box mt={2} ml={2} p={2} bg="orange.100" borderRadius="md">
              <Text fontSize="xs" color="orange.800" fontWeight="bold">
                You've used all 10 free AI queries. Upgrade to a paid plan for unlimited access.
              </Text>
            </Box>
          )}
          <Flex justifyContent="flex-end">
            <HStack pt={4} spacing={2}>
              <Button
                ref={cancelRef}
                minWidth="100px"
                borderRadius="md"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                colorScheme="gray"
                minWidth="100px"
                borderRadius="md"
                isDisabled={!selectedFile || tradeLoading || csvFixLoading}
                onClick={() => onConfirm()}
                isLoading={tradeLoading && !csvFixLoading}
              >
                Default Update
              </Button>
              <Button
                colorScheme="blue"
                minWidth="100px"
                borderRadius="md"
                isDisabled={!selectedFile || csvFixLoading || csvQuotaExhausted}
                onClick={() => onFixWithAI()}
                isLoading={csvFixLoading}
                loadingText="AI Update"
              >
                AI Update
              </Button>
            </HStack>
          </Flex>
        </Stack>
    </Box>
  );
};

export default BulkUpdateCsvView;
